import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
} from "@mui/material";
// Using native HTML datetime-local input instead of MUI DateTimePicker to avoid additional dependencies
import { format } from "date-fns";
import { Parse } from "parse";
import * as XLSX from "xlsx";
import CustomPagination from "../../Components/CustomPagination";
import { useSearchParams } from "react-router-dom";

const TransactionReports = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL params
  const initializeFilters = () => {
    return {
      customer: searchParams.get('customer') || "",
      startDate: searchParams.get('startDate') ? new Date(searchParams.get('startDate')) : null,
      endDate: searchParams.get('endDate') ? new Date(searchParams.get('endDate')) : null,
      displayZeroData: searchParams.get('displayZeroData') === 'true',
      displayAllAccounts: searchParams.get('displayAllAccounts') === 'true',
      quickFilter: searchParams.get('quickFilter') || null,
    };
  };

  // State for filters
  const [filters, setFilters] = useState(initializeFilters);

  // State for data
  const [summaryData, setSummaryData] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value,
      ...(field === 'quickFilter' && value ? { startDate: null, endDate: null } : {})
    };

    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.customer) params.set('customer', newFilters.customer);
    if (newFilters.startDate) params.set('startDate', format(newFilters.startDate, 'yyyy-MM-dd'));
    if (newFilters.endDate) params.set('endDate', format(newFilters.endDate, 'yyyy-MM-dd'));
    if (newFilters.displayZeroData) params.set('displayZeroData', 'true');
    if (newFilters.displayAllAccounts) params.set('displayAllAccounts', 'true');
    if (newFilters.quickFilter) params.set('quickFilter', newFilters.quickFilter);

    setSearchParams(params);
  };

  // Quick filter handlers
  const handleQuickFilter = (filterType) => {
    handleFilterChange('quickFilter', filterType);
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = {
      customer: "",
      startDate: null,
      endDate: null,
      displayZeroData: false,
      displayAllAccounts: false,
      quickFilter: null,
    };

    setFilters(clearedFilters);
    setSearchParams(new URLSearchParams()); // Clear URL params
    setSummaryData(null);
    setDetailData([]);
    setHasSearched(false);
    setError("");
  };

  // Search function
  const handleSearch = async () => {
    if (!Parse.User.current()) {
      setError("Please log in to view reports");
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const params = {
        startDate: filters.startDate ? filters.startDate.toISOString() : null,
        endDate: filters.endDate ? filters.endDate.toISOString() : null,
        accountName: filters.customer || null,
        quickFilter: filters.quickFilter,
        displayZeroData: filters.displayZeroData,
      };

      // Get summary data from cache using unified API
      const summaryParams = {
        ...params,
        getSummary: true,
      };
      const summaryResult = await Parse.Cloud.run("getTransactionReport", summaryParams);
      setSummaryData(summaryResult); // Can be null if zero data and displayZeroData is false

      // Get detail data only if "Display all accounts" is ON
      if (filters.displayAllAccounts) {
        const detailParams = {
          ...params,
          displayZeroData: filters.displayZeroData,
          displayAllAccounts: filters.displayAllAccounts,
          getDetails: true,
        };
        const detailResult = await Parse.Cloud.run("getTransactionReport", detailParams);
        setDetailData(detailResult.data || []);
        setTotal(detailResult.total || 0);
      } else {
        // Clear detail data when switch is OFF
        setDetailData([]);
        setTotal(0);
      }
      setPage(1); // Reset to first page on new search

    } catch (err) {
      console.error("❌ Error fetching reports:", err);

      // Handle access denied errors gracefully by showing "No info!" in the table
      if (err.message && err.message.includes("Access denied")) {
        // Create a summary data object with "No info!" message
        setSummaryData({
          startTime: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
          endTime: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A',
          account: "No info!",
          totalRecharged: 0,
          totalRedeemed: 0,
          totalIncome: 0,
          holdingPercentage: 0,
          isError: true,
        });

        // Clear detail data
        setDetailData([]);
        setTotal(0);
        setError(""); // Clear error to prevent alert display
      } else {
        // For other errors, show as alert
        setError(err.message || "Failed to fetch reports");
        setSummaryData(null);
        setDetailData([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // Export Reports Summary to Excel
  const handleSummaryExcelExport = () => {
    if (!summaryData || summaryData.requiresUsername || summaryData.isError) {
      setError("No summary data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([summaryData]);
    XLSX.utils.book_append_sheet(wb, summarySheet, "Reports Summary");
    XLSX.writeFile(wb, `Reports_Summary_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);
  };

  // Export Detail Reports to Excel
  const handleDetailExcelExport = () => {
    if (detailData.length === 0) {
        setError("No detail data to export");
        return;
      }

      const wb = XLSX.utils.book_new();
    const detailSheet = XLSX.utils.json_to_sheet(detailData);
      XLSX.utils.book_append_sheet(wb, detailSheet, "Detail Reports");
      XLSX.writeFile(wb, `Detail_Reports_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);
  };

  // Pagination helpers
  const getPaginatedData = () => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return detailData.slice(startIndex, endIndex);
  };

  return (
    <Box
      sx={{
        mt: 4,
        backgroundColor: "#242424",
        borderRadius: 2,
        boxShadow: "none !important",
        padding: isSmallScreen ? { xs: 1, sm: 1 } : { xs: 1.5, sm: 2 },
      }}
    >


      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Username Field */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search by Username"
              placeholder="Enter username"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: '#ccc',
                },
              }}
            />
          </Grid>

          {/* Period Fields */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="datetime-local"
              value={filters.startDate ? format(filters.startDate, 'yyyy-MM-dd\'T\'HH:mm') : ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value) : null)}
              disabled={!!filters.quickFilter}
              inputProps={{
                max: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: '#ccc',
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="datetime-local"
              value={filters.endDate ? format(filters.endDate, 'yyyy-MM-dd\'T\'HH:mm') : ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value) : null)}
              disabled={!!filters.quickFilter}
              inputProps={{
                max: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'),
                min: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd\'T\'HH:mm') : undefined,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#333',
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: '#ccc',
                },
              }}
            />
          </Grid>

          {/* Search, Clear and Excel Buttons */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  background: "#FFC107",
                  color: "#000",
                  textTransform: "none",

                  "&:hover": {
                    backgroundColor: "#FFB300",
                  },
                  "&:disabled": {
                    backgroundColor: "#666",
                    color: "#999",
                  },
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#FFC107",
                  color: "#fff",
                  textTransform: "none",

                  "&:hover": {
                    borderColor: "#FFB300",
                    backgroundColor: "rgba(255, 193, 7, 0.1)",
                  },
                }}
              >
                Clear
              </Button>

            </Box>
          </Grid>
        </Grid>

        {/* Toggle Switches */}
        <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filters.displayZeroData}
                onChange={(e) => handleFilterChange('displayZeroData', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#FFC107',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#FFC107',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ color: 'white' }}>
                Display zero data account
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={filters.displayAllAccounts}
                onChange={(e) => handleFilterChange('displayAllAccounts', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#FFC107',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#FFC107',
                  },
                }}
              />
            }
            label={
              <Typography sx={{ color: 'white' }}>
                Display all accounts
              </Typography>
            }
          />
        </Box>

        {/* Quick Filter Buttons */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['today', 'yesterday', 'thisWeek', 'lastWeek'].map((filterType) => (
            <Button
              key={filterType}
              variant={filters.quickFilter === filterType ? "contained" : "outlined"}
              size="small"
              onClick={() => handleQuickFilter(filterType)}
              sx={{
                textTransform: "capitalize",
                color: filters.quickFilter === filterType ? "#000" : "#FFC107",
                borderColor: "#FFC107",
                backgroundColor: filters.quickFilter === filterType ? "#FFC107" : "transparent",
                "&:hover": {
                  backgroundColor: filters.quickFilter === filterType ? "#FFA000" : "rgba(255, 193, 7, 0.1)",
                },
              }}
            >
              {filterType === 'thisWeek' ? 'This Week' : filterType === 'lastWeek' ? 'Last Week' : filterType}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Reports Summary Section */}
      {hasSearched && summaryData && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#FFC107",
                fontWeight: "bold",
              }}
            >
              Reports Summary
            </Typography>
            <Button
              variant="contained"
              onClick={handleSummaryExcelExport}
              disabled={!summaryData || summaryData.requiresUsername || summaryData.isError}
              sx={{
                background: "#4CAF50",
                color: "white",
                textTransform: "none",
                fontSize: "12px",
                padding: "6px 12px",
                "&:hover": {
                  backgroundColor: "#45a049",
                },
                "&:disabled": {
                  backgroundColor: "#666",
                  color: "#999",
                },
              }}
            >
              Export Excel
            </Button>
          </Box>
          <Typography
            variant="body2"
            sx={{ color: "#ccc", mb: 2 }}
          >
            {summaryData.startTime || 'N/A'} - {summaryData.endTime || 'N/A'}
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: "#2a2a2a",
              boxShadow: "none",
              borderRadius: "8px",
              overflow: "hidden",
              "& .MuiTableCell-root": {
                color: "white",
                borderBottom: "1px solid #333",
                padding: "12px 16px",
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{
                  "& .MuiTableCell-root": {
                    borderBottom: "none !important",
                  }
                }}>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Start Time</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>End Time</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Account</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Total Recharged</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Total Redeemed</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Total Income</TableCell>
                  <TableCell sx={{
                    color: "#FFC107 !important",
                    fontWeight: "700",
                    fontSize: "12px",

                    letterSpacing: "0.5px",
                    borderBottom: "none"
                  }}>Holding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.requiresUsername || summaryData.isError ? (
                  // Show message row when username is required or there's an error
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: summaryData.isError ? "#ccc" : "#FFC107",
                        fontSize: "16px",
                        fontWeight: "bold"
                      }}
                    >
                      {summaryData.account}
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show normal summary data row
                  <TableRow sx={{ "&:hover": { backgroundColor: "#2a2a2a" } }}>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.startTime || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.endTime || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.account}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.totalRecharged?.toLocaleString() || '0'}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.totalRedeemed?.toLocaleString() || '0'}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.totalIncome?.toLocaleString() || '0'}</TableCell>
                    <TableCell sx={{ fontSize: "13px" }}>{summaryData.holdingPercentage?.toFixed(2) || '0'}%</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Detail Reports Section - Only show when "Display all accounts" is ON */}
      {hasSearched && filters.displayAllAccounts && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#FFC107",
                fontWeight: "bold",
              }}
            >
              Detail Reports
            </Typography>
            <Button
              variant="contained"
              onClick={handleDetailExcelExport}
              disabled={detailData.length === 0}
              sx={{
                background: "#4CAF50",
                color: "white",
                textTransform: "none",
                fontSize: "12px",
                padding: "6px 12px",
                "&:hover": {
                  backgroundColor: "#45a049",
                },
                "&:disabled": {
                  backgroundColor: "#666",
                  color: "#999",
                },
              }}
            >
              Export Excel
            </Button>
          </Box>

          {detailData.length > 0 ? (
            <>
              <Typography
                variant="body2"
                sx={{ color: "#ccc", mb: 2 }}
              >
                {detailData[0]?.startTime || 'N/A'} - {detailData[0]?.endTime || 'N/A'}
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: "#2a2a2a",
                  boxShadow: "none",
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& .MuiTableCell-root": {
                    color: "white",
                    borderBottom: "1px solid #333",
                    padding: "12px 16px",
                  },
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{
                      "& .MuiTableCell-root": {
                        borderBottom: "none !important",
                      }
                    }}>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Start Time</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>End Time</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Account</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Role</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Total Recharged</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Total Redeemed</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Total Income</TableCell>
                      <TableCell sx={{
                        color: "#FFC107 !important",
                        fontWeight: "700",
                        fontSize: "12px",

                        letterSpacing: "0.5px",
                        borderBottom: "none"
                      }}>Holding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPaginatedData().map((row, index) => (
                      <TableRow key={index} sx={{ "&:hover": { backgroundColor: "#2a2a2a" } }}>
                        <TableCell sx={{ fontSize: "13px" }}>{row.startTime || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.endTime || 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.account}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.role}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.totalRecharged?.toLocaleString() || '0'}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.totalRedeemed?.toLocaleString() || '0'}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.totalIncome?.toLocaleString() || '0'}</TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>{row.holdingPercentage?.toFixed(2) || '0'}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Custom Pagination */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100% !important",
                  margin: "16px 0px",
                }}
              >
                <CustomPagination
                  page={page}
                  perPage={perPage}
                  total={detailData.length}
                  setPage={setPage}
                  setPerPage={setPerPage}
                />
              </Box>
            </>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: "#2a2a2a",
                boxShadow: "none",
                borderRadius: "8px",
                overflow: "hidden",
                "& .MuiTableCell-root": {
                  color: "white",
                  borderBottom: "1px solid #333",
                  padding: "12px 16px",
                },
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "none !important",
                    }
                  }}>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Start Time</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>End Time</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Account</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Role</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Total Recharged</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Total Redeemed</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Total Income</TableCell>
                    <TableCell sx={{
                      color: "#FFC107 !important",
                      fontWeight: "700",
                      fontSize: "12px",

                      letterSpacing: "0.5px",
                      borderBottom: "none"
                    }}>Holding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "#ccc",
                        fontSize: "16px",
                        fontWeight: "bold"
                      }}
                    >
                      {loading ? "Loading..." : "No info!"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TransactionReports;
